/**
 * Checks report their coverage (lesson 12): a check that covered nothing is never a pass,
 * and an incomplete check is never shown as all clear.
 * @module
 */
import { formatCount } from '../format/index.js';

/** Outcome of a check run (matches `check_run.result`). */
export const CHECK_STATUSES = ['pass', 'fail', 'partial', 'could_not_run'] as const;
/** One of {@link CHECK_STATUSES}. */
export type CheckStatus = (typeof CHECK_STATUSES)[number];

/** Counts a check reports. */
export interface Coverage {
  /** Items the check should have looked at. */
  expected: number;
  /** Items it actually looked at. */
  checked: number;
  /** Checked items that failed. */
  failures: number;
}

function assertCount(name: string, n: number): void {
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError(`${name} must be an integer >= 0`);
}

/**
 * The status a check's counts justify, in this order:
 * 1. `checked === 0` → `could_not_run` (even when nothing was expected: never a silent pass);
 * 2. `failures > 0` → `fail` (a known failure is never softened to `partial`);
 * 3. `checked < expected` → `partial`;
 * 4. otherwise `pass`.
 *
 * Throws `RangeError` on impossible counts (negative, `checked > expected`,
 * `failures > checked`): those are bugs in the check, not results.
 */
export function coverageStatus({ expected, checked, failures }: Coverage): CheckStatus {
  assertCount('expected', expected);
  assertCount('checked', checked);
  assertCount('failures', failures);
  if (checked > expected) throw new RangeError('checked cannot exceed expected');
  if (failures > checked) throw new RangeError('failures cannot exceed checked');
  if (checked === 0) return 'could_not_run';
  if (failures > 0) return 'fail';
  if (checked < expected) return 'partial';
  return 'pass';
}

/** A counted exception in a coverage sentence, e.g. `{ count: 12, label: 'could not classify' }`. */
export interface CoverageException {
  count: number;
  label: string;
}

/** Input for {@link describeCoverage}. */
export interface DescribeCoverageInput {
  expected: number;
  checked: number;
  /** Plural noun for the items (default `items`). */
  noun?: string;
  /** What happened to checked items (default `accounted for`). */
  verb?: string;
  /** Exceptions to list after the headline; zero counts are left out. */
  exceptions?: readonly CoverageException[];
}

/**
 * One-line coverage sentence for reports and the Admin, e.g.
 * `1 445 of 1 445 items accounted for; 12 could not classify`. Counts use the same
 * grouping as money (non-breaking spaces; one number style, D-022).
 */
export function describeCoverage({
  expected,
  checked,
  noun = 'items',
  verb = 'accounted for',
  exceptions = [],
}: DescribeCoverageInput): string {
  assertCount('expected', expected);
  assertCount('checked', checked);
  const parts = [`${formatCount(checked)} of ${formatCount(expected)} ${noun} ${verb}`];
  for (const e of exceptions) {
    assertCount('exception count', e.count);
    if (e.count > 0) parts.push(`${formatCount(e.count)} ${e.label}`);
  }
  return parts.join('; ');
}

/**
 * A share as a percentage for reports (review UI2A-301): one decimal, rounded **down**, so a
 * figure never reads better than it is; it reads `100` only when `part === whole` (every
 * item agrees), never for 24 999 of 25 000. `null` when there is nothing to divide by.
 * Every percentage the replay report shows comes from this one rule.
 */
export function sharePercent(part: number, whole: number): number | null {
  assertCount('part', part);
  assertCount('whole', whole);
  if (part > whole) throw new RangeError('part cannot exceed whole');
  if (whole === 0) return null;
  if (part === whole) return 100;
  return Math.min(99.9, Math.floor((part * 1000) / whole) / 10);
}

/** The VAT comparison's counts (as in the replay report's `vat`). */
export interface VatAgreementCounts {
  /** Every receipt line. */
  lines: number;
  /** Lines compared (mapped, VAT worked out). */
  checked: number;
  /** Of those, agreeing with the source. */
  matched: number;
  /** Of those, differing from the source. */
  mismatched: number;
  /** Mapped lines whose VAT couldn't be worked out. */
  errors: number;
}

const counted = (n: number, one: string, many: string) =>
  `${formatCount(n)} ${n === 1 ? one : many}`;

/**
 * The VAT check's one line, in counts over **all** lines (review UI2A-301), e.g.
 * `VAT agrees with the source on 24 860 of 24 992 lines; 32 not compared (unmapped);
 * 97 differ (listed); 3 couldn’t be worked out (listed)`. No percentage: the report's tile
 * shows {@link sharePercent} of the same counts, so the two can't disagree.
 */
export function describeVatCheck(vat: VatAgreementCounts): string {
  const { lines, checked, matched, mismatched, errors } = vat;
  for (const [k, v] of Object.entries({ lines, checked, matched, mismatched, errors })) {
    assertCount(k, v);
  }
  if (checked === 0 && errors === 0) return 'VAT could not be compared: no mapped lines';
  const notCompared = Math.max(0, lines - checked - errors);
  const parts = [
    `VAT agrees with the source on ${formatCount(matched)} of ${counted(lines, 'line', 'lines')}`,
  ];
  if (notCompared > 0) parts.push(`${formatCount(notCompared)} not compared (unmapped)`);
  if (mismatched > 0) parts.push(`${counted(mismatched, 'differs', 'differ')} (listed)`);
  if (errors > 0) parts.push(`${formatCount(errors)} couldn’t be worked out (listed)`);
  return parts.join('; ');
}
